// @flow
import React from 'react';
import { Text, View, Button, ActivityIndicator } from 'react-native';
import { connect } from 'react-redux';
import styled from 'styled-components/native';
import { User } from 'types';
import colors from 'styles/colors.js';
import snip from 'styles/snip.js';
import { logout } from 'actions/session';
import { updateProfile } from 'actions/session';
import { showAlert } from 'actions/alert';
import { fetchInspections } from 'actions/inspection';
import { fetchAttestations } from 'actions/attestation';
import OS from 'utils/offlineStore.js';
import { isOnline } from 'utils';
import Avatar from 'components/Avatar';
import LinkButton from 'components/LinkButton';
import Input from 'components/Input';
import Dialog from 'components/Dialog';

const UserItem = styled(View)`
	flex-direction:   row;
	padding:          ${snip.pad};
	margin-bottom:    0px;
	background-color: #fff;
`;

type Props = {
	user: User,
	logout: Function,
	updateProfile: Function,
	showAlert: Function,
	fetchInspections: Function,
	fetchAttestations: Function,
};

type State = {
	name: string,
	email: string,
	phone: string,
	password: string,
	password_confirmation: string,
	verrs: Object,
	isResetting: boolean,
	showConfirmDialog: boolean,
};

class Profile extends React.Component {

	props: Props;
	state: State;

	constructor(props: Props) {

		super(props);

		this.state = {
			name: props.user.name,
			email: props.user.email,
			phone: props.user.phone,
			password: '',
			password_confirmation: '',
			verrs: {},
			isResetting: false,
			showConfirmDialog: false,
		};
	}

	onUpdateProfile = (evt) => {

		let userNew = { ...this.state };

		// FILTER PWD FIELDS IF EMPTY
		const pwf = ['password', 'password_confirmation'];
		pwf.forEach((k) => {
			if ((typeof userNew[k] !== 'string') || (userNew[k].length === 0))
				delete userNew[k];
		});

		return this.props.updateProfile(this.props.user.id, { user: userNew })
			.then((ret) => {

				// CLEAR PASSWORD FIELDS
				this.setState({ password: '', password_confirmation: '', verrs: {} });

				// PROMPT SUCCESS
				this.props.showAlert('Profile Updated', '', 'ok');

			})
			.catch((err) => {

				// REPORT VERRS
				if (err.hasOwnProperty('verr'))
					this.setState({ verrs: err.verr });

				return Promise.reject(err);
			});
	};

	onResetAndSync = () => {
		if (this.state.isResetting) return;

		if (!isOnline()) {
			this.props.showAlert(
				'An internet connection is required to re-fetch and restore data from the server. Please reconnect and try again.',
				'No Internet Connection',
				'err'
			);
			return;
		}

		this.setState({ showConfirmDialog: true });
	};

	onConfirmReset = async () => {
		this.setState({ showConfirmDialog: false, isResetting: true });

		// Unblock the UI and the background sync loop up-front, so a stuck
		// upload can't keep the "uploading" banner on during the reset.
		localStorage.setItem('isUploading', 'false');

		try {
			// 1. Clear the stuck offline upload queue (IndexedDB).
			await OS.Store('pendingAPI').clear();

			// 2. Re-pull the authoritative inspections & attestations from the server.
			//    bForceAjax=true makes any network failure reject here instead of
			//    silently falling back to the stale offline cache, and also refreshes
			//    the OS/LS caches + redux state (FETCH_*_SUCCESS) inside each action.
			const userId = this.props.user ? this.props.user.id : null;

			if (userId) {
				await Promise.all([
					this.props.fetchInspections(userId, true),
					this.props.fetchAttestations(userId, true),
				]);

				// 3. Signal downstream scenes (ShowInspection / ShowAttestation) to
				//    re-read the freshly-synced data.
				localStorage.setItem('reloadInspection', 'true');

				// 4. Reset this scene's own transient form state.
				this.setState({ password: '', password_confirmation: '', verrs: {} });

				this.props.showAlert(
					'Current queue cleared and latest inspection data synchronized from the server.',
					'Reset Complete',
					'ok'
				);
			} else {
				// No user id means we could NOT re-fetch/reset app state; only the
				// queue was cleared, so don't claim a full reset.
				this.props.showAlert(
					'The offline upload queue was cleared, but data could not be re-synced because no active user was found. Please log out and back in.',
					'Partially Reset',
					'err'
				);
			}
		} catch (err) {
			console.error('Failed to reset and sync:', err);
			this.props.showAlert(
				'Failed to restore data from server. Please check your connection and try again.',
				'Reset Failed',
				'err'
			);
		} finally {
			// Always leave the global uploading flag consistent so the banner
			// can't get stuck if the sync threw midway.
			localStorage.setItem('isUploading', 'false');
			this.setState({ isResetting: false });
		}
	};

	render() {

		const { user, logout } = this.props;

		let fnPush = (route) => this.props.history.push(route);

		const arFields = [
			{ label: 'Name', key: 'name' },
			{ label: 'E-Mail', key: 'email' },
			{ label: 'Telephone', key: 'phone' },
			{ label: 'Password', key: 'password' },
			{ label: 'Password Conf', key: 'password_confirmation' },
		];

		return (
			<View>
				<Dialog
					visible={this.state.showConfirmDialog}
					confirm={true}
					message="This will clear any stuck offline uploads and re-sync your inspections and attestations from the server. Do you want to proceed?"
					onAccept={this.onConfirmReset}
					onReject={() => this.setState({ showConfirmDialog: false })}
				/>
				<Dialog
					visible={this.state.isResetting}
					message="Clearing stuck offline uploads and synchronizing data from the server. Please wait..."
					uploading={true}
				/>
				<UserItem>
					<Avatar email={user.email} />
					<View style={{ marginLeft: '0.7em', paddingTop: '0.1em' }}>
						<Text style={{ marginBottom: '0.25em' }}>{user.name}</Text>
						<Text style={{ fontSize: '0.9em' }}>{user.email}</Text>
					</View>
				</UserItem>
				<View>
					{
						arFields.map((V, ix) => (
							<Input
								key={ix}
								label={V.label}
								verr={this.state.verrs[V.key]}
								value={this.state[V.key]}
								onChangeText={(value) => this.setState({ [V.key]: value })}
							/>
						))
					}
					<View style={{ padding: '0.5em', paddingLeft: '70%' }}>
						<Button
							title="Update"
							onPress={this.onUpdateProfile}
							color={colors.green_primary}
						/>
					</View>
				</View>

				<LinkButton
					style={{ marginTop: '2em' }}
					onPress={this.onResetAndSync}
					disabled={this.state.isResetting}
				>
					<View style={{ flexDirection: 'row', alignItems: 'center' }}>
						{this.state.isResetting && (
							<ActivityIndicator size="small" color={colors.orange} style={{ marginRight: '0.5em' }} />
						)}
						<Text style={{ fontWeight: 'bold', color: colors.orange }}>
							{this.state.isResetting ? 'Clearing & Restoring...' : 'Clear Stuck Uploads & Reset'}
						</Text>
					</View>
				</LinkButton>

				<LinkButton
					style={{ marginTop: '1em', marginBottom: '1em' }}
					onPress={() => {
						return logout().finally(() => { fnPush('/'); });
					}}
				>
					<Text style={{ fontWeight: 'bold', color: colors.red }}>Logout</Text>
				</LinkButton>
			</View>
		);
	}
}

export default connect(
	(state) => {
		return { user: state.session.currentUser };
	},
	{
		logout,
		updateProfile,
		showAlert,
		fetchInspections,
		fetchAttestations,
	}
)(Profile);

